import { ActionTree, ActionContext } from 'vuex'
import { IPlan, IPriceDetails, ITaxDetails, IProductExtended, IRootState } from '@/@types'
import CONSTANTS from '@/constants'

const {
  BLACK_FRIDAY_PRODUCT_BUNDLES,
  PLANS,
  VOUCHERS,
  LIVE_VIRTUAL_TRAININGS,
  CYBER_MONDAY_SAVINGS_COUPONS,
} = CONSTANTS
const { INE_BLACK_FRIDAY_BUNDLE_THREE } = BLACK_FRIDAY_PRODUCT_BUNDLES
const BLACKFRIDAY_ITEMS = [
  PLANS.ANNUAL,
  VOUCHERS.EJPT,
  VOUCHERS.EDA,
  VOUCHERS.ECDFP,
  VOUCHERS.ECIR,
  VOUCHERS.EJDS,
  VOUCHERS.ECPPT,
  VOUCHERS.ECTHP,
  VOUCHERS.EMAPT,
  VOUCHERS.EWPT,
]
const CYBERMONDAY_ITEMS = [PLANS.SKILL_DIVE_ANNUAL]

const receivesAutomaticDiscount = (item?: IProductExtended) => {
  const receivesDiscount = item?.user_receives_discount
  if (typeof receivesDiscount === 'string') {
    return receivesDiscount === 'true'
  }
  return receivesDiscount === true
}

const getCouponTargetCodes = (coupon: any) => {
  return (
    [
      ...((coupon?.items as { code: string }[]) || []),
      ...((coupon?.plans as { code: string }[]) || []),
    ]
      .map(item => item.code)
      // filter(Boolean) narrows the type to string
      .filter((code): code is string => Boolean(code))
  )
}

const normalizeCouponStack = (stack: any) => {
  if (!Array.isArray(stack)) {
    return []
  }
  return stack.map(code => (typeof code === 'string' ? code.toLowerCase() : '')).filter(Boolean)
}

const hasCyberMondaySavingsCoupon = (stack: any) => {
  const normalized = normalizeCouponStack(stack)
  if (!normalized.length) {
    return false
  }
  return normalized.some(code => CYBER_MONDAY_SAVINGS_COUPONS.includes(code))
}

const isVoucherAddOnWithDiscount = (
  item: IProductExtended | undefined,
  { isPromotionActive, hasSavingsCoupon }: { isPromotionActive: boolean; hasSavingsCoupon: boolean }
) => {
  if (!item || item.merchant_id === VOUCHERS.ICCA) {
    return false
  }
  const merchantId = item.merchant_id
  const isAddOn = Boolean((item as any)?.addOn)
  const isVoucher = Object.values(VOUCHERS).includes(merchantId)
  const hasPromoContext = isPromotionActive || hasSavingsCoupon
  return Boolean(isVoucher && isAddOn && hasPromoContext && Number(item.discounted_price_cents) > 0)
}

const actions: ActionTree<IPriceDetails, IRootState> = {
  setCurrencySymbol: (
    context: ActionContext<IPriceDetails, IRootState>,
    currencySymbol: string
  ) => {
    context.commit('CURRENCY_SYMBOL_UPDATED', currencySymbol)
  },
  setCurrencyCode: (context: ActionContext<IPriceDetails, IRootState>, currencyCode: string) => {
    context.commit('CURRENCY_CODE_UPDATED', currencyCode)
  },
  setDiscount: (context: ActionContext<IPriceDetails, IRootState>, discount: number) => {
    context.commit('DISCOUNT_UPDATED', discount)
  },
  setSubtotal: (context: ActionContext<IPriceDetails, IRootState>, subtotal: number) => {
    context.commit('SUBTOTAL_UPDATED', subtotal)
  },
  setTaxes: (context: ActionContext<IPriceDetails, IRootState>, taxes: number) => {
    context.commit('TAXES_UPDATED', taxes)
  },
  setTotal: (context: ActionContext<IPriceDetails, IRootState>, total: number) => {
    context.commit('TOTAL_UPDATED', total)
  },
  setShowPromotionSavings: (
    context: ActionContext<IPriceDetails, IRootState>,
    showPromotionSavings: boolean
  ) => {
    context.commit('SHOW_PROMOTION_SAVINGS_UPDATED', showPromotionSavings)
  },

  priceDetailsUpdate: async (context: ActionContext<IPriceDetails, IRootState>, payload: any) => {
    const shoppingCart = context.rootGetters['shoppingCart/getShoppingCart'] || []
    const recurlyPricing = context.rootGetters['recurly/getRecurlyPricing'] || null
    const isBusinessPlanExpired = context.rootGetters['business/isBusinessPlanExpired']
    const isPromotionActive = context.rootGetters['app/isPromotionActive']
    const isCyberMondayActive = context.rootGetters['app/isCyberMondayActive']
    const couponStrStack = context.rootGetters['coupons/getCouponStrStack'] || []
    const couponTotalDiscount = Number(context.rootGetters['coupons/getCouponTotalDiscount']) || 0
    const cartHasAnySubscription = context.rootGetters['shoppingCart/cartHasAnySubscription']
    const is100PercentDiscount = context.rootGetters['coupons/is100PercentOffCoupon']
    const couponObjStack = context.rootGetters['coupons/getCouponObjStack'] || []
    const hasSavingsCoupon = hasCyberMondaySavingsCoupon(couponStrStack)
    const lvtMerchantIds = Object.values(LIVE_VIRTUAL_TRAININGS)
    const cartHasDiscountedLvtItem = shoppingCart.some((item: IProductExtended) => {
      return lvtMerchantIds.includes(item.merchant_id) && receivesAutomaticDiscount(item)
    })
    const couponTargetsLvtItem = couponObjStack.some((coupon: any) => {
      if (!coupon?.isEligibleForCart) {
        return false
      }
      const couponCodes = getCouponTargetCodes(coupon)
      if (!couponCodes.length) {
        return false
      }
      return shoppingCart.some((item: IProductExtended) => {
        return couponCodes.includes(item.merchant_id) && lvtMerchantIds.includes(item.merchant_id)
      })
    })
    const couponTargetsNonDiscountedCartItem = couponObjStack.some((coupon: any) => {
      if (!coupon?.isEligibleForCart) {
        return false
      }
      const couponCodes = getCouponTargetCodes(coupon)
      if (!couponCodes.length) {
        return false
      }
      return shoppingCart.some((item: IProductExtended) => {
        if (!couponCodes.includes(item.merchant_id)) {
          return false
        }
        return !receivesAutomaticDiscount(item)
      })
    })

    let itemHasPromoDiscount = false
    let subtotal = 0
    let discount = 0
    let taxes = 0
    let taxesObjArray = []
    let total = 0

    if (!!recurlyPricing && !!recurlyPricing.price) {
      const {
        currency: { symbol },
        now: { taxes: taxesRecurly },
        taxes: recurlyTaxesObjArray,
      } = recurlyPricing.price

      taxesObjArray = recurlyTaxesObjArray
      if (symbol && symbol.length) {
        context.dispatch('setCurrencySymbol', symbol)
        taxes = Number(taxesRecurly)
      }
    }
    taxes = taxes ? taxes * 100 : taxes
    let isCertWithPossibleDiscount
    shoppingCart.forEach((item: IProductExtended) => {
      isCertWithPossibleDiscount =
        Object.values(VOUCHERS).includes(item.merchant_id) && item.merchant_id !== VOUCHERS.ICCA
      if (isPromotionActive && isCertWithPossibleDiscount && cartHasAnySubscription) {
        itemHasPromoDiscount = true
        discount += (item.price_cents - item.discounted_price_cents!) / 100
      } else if (item.user_receives_discount && !isBusinessPlanExpired && isPromotionActive) {
        itemHasPromoDiscount = true
      }
      if (isVoucherAddOnWithDiscount(item, { isPromotionActive, hasSavingsCoupon })) {
        itemHasPromoDiscount = true
      }

      // Apply discount for live virtual training items if the user is eligible.
      if (lvtMerchantIds.includes(item.merchant_id) && item.user_receives_discount) {
        discount += item.price_cents - item.discounted_price_cents!
      }

      subtotal += item.price_cents

      if (item?.addOnsOnCart?.length) {
        item.addOnsOnCart.forEach((addOn: any) => {
          subtotal +=
            addOn.user_receives_discount && isPromotionActive!
              ? addOn.discounted_price_cents!
              : addOn.price_cents
        })
      }
      if (item?.pre_sale) itemHasPromoDiscount = true
    })

    if (itemHasPromoDiscount) {
      const promoDiscount = await context.dispatch('promoProductBundlePricing')
      const hasPromoSavings = promoDiscount > 0
      context.dispatch('setShowPromotionSavings', hasPromoSavings)
      if (hasPromoSavings) {
        discount = promoDiscount
      } else {
        itemHasPromoDiscount = false
      }
    } else {
      context.dispatch('setShowPromotionSavings', false)
    }

    // if true, means that the user has enter a valid coupon, so we add that value to discount
    // discount might have some previous value from this.promoProductBundlePricing execution
    let isCouponElegible = true
    if (isPromotionActive) {
      const canStackCouponWithLvtDiscount = cartHasDiscountedLvtItem || couponTargetsLvtItem
      const canStackCouponWithOtherDiscounts =
        canStackCouponWithLvtDiscount || couponTargetsNonDiscountedCartItem
      isCouponElegible =
        is100PercentDiscount || !itemHasPromoDiscount || canStackCouponWithOtherDiscounts
    }

    if (couponTotalDiscount !== 0 && isCouponElegible) {
      discount += couponTotalDiscount
    }

    // Cap the discount at the subtotal (both in cents) so a coupon worth more than
    // the cart floors the total at 0 and keeps the itemized breakdown (subtotal,
    // discount, taxes) consistent instead of showing negative tax/total values.
    discount = Math.min(discount, subtotal)

    if (taxes) {
      // if the user has a discount on several items, recurly does not take that into account for taxes calculation
      // this is why we calculate the taxes manually after the discount is calculated
      taxes = taxesObjArray.reduce((acc: number, taxObj: ITaxDetails) => {
        return acc + (subtotal - discount) * Number(taxObj.rate)
      }, 0)
    }
    total = subtotal + taxes - discount

    context.dispatch('setDiscount', discount / 100)
    context.dispatch('setSubtotal', subtotal / 100)
    context.dispatch('setTaxes', taxes / 100)
    context.dispatch('setTotal', total / 100)
  },
  promoProductBundlePricing: (context: ActionContext<IPriceDetails, IRootState>) => {
    const cartItems = []
    const shoppingCart = context.rootGetters['shoppingCart/getShoppingCart']
    const availablePlans = context.rootGetters['products/availablePlans']
    const cartHasAnySubscription = context.rootGetters['shoppingCart/cartHasAnySubscription']
    const isPromotionActive = context.rootGetters['app/isPromotionActive']
    const couponStrStack = context.rootGetters['coupons/getCouponStrStack'] || []
    const hasSavingsCoupon = hasCyberMondaySavingsCoupon(couponStrStack)

    let discount = 0
    shoppingCart.forEach((item: IProductExtended) => {
      cartItems.push(item.merchant_id || item.plan_code)
      if (item.slug === INE_BLACK_FRIDAY_BUNDLE_THREE || item.merchant_id === PLANS.ANNUAL) {
        const fundamentalsAnnual = availablePlans.find(
          (plan: IPlan) => plan.merchant_id && plan.merchant_id === PLANS.ANNUAL
        )
        discount += fundamentalsAnnual.price_cents - fundamentalsAnnual.discounted_price_cents
      } else {
        if (
          (item.discounted_price_cents && receivesAutomaticDiscount(item)) ||
          (isPromotionActive && cartHasAnySubscription && item.discounted_price_cents) ||
          isVoucherAddOnWithDiscount(item, { isPromotionActive, hasSavingsCoupon })
        ) {
          discount += item.price_cents - (item.discounted_price_cents || 0)
        }
      }
    })
    return discount
  },
  showBlackFridaySavings: (
    context: ActionContext<IPriceDetails, IRootState>,
    itemFromCart: IProductExtended
  ) => {
    const couponDiscount = context.rootGetters['coupons/getCouponTotalDiscount']
    const couponApplied = Number(couponDiscount) !== 0
    const bfProduct = BLACKFRIDAY_ITEMS.includes(itemFromCart.merchant_id)
    const cmProduct = CYBERMONDAY_ITEMS.includes(itemFromCart.merchant_id)

    return !couponApplied && (bfProduct || cmProduct)
  },
}

export default actions
